import { Link } from "react-router-dom";
import { ArrowRight, Smartphone, Radio, Sparkles, Play, Trophy, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/site/Reveal";
import SectionHeading from "@/components/site/SectionHeading";

const STEPS = [
  { n: "01", icon: Smartphone, tone: "am-tile-pink",     title: "Start the app",     copy: "Fire up AudioMine on your phone and aim it at the source of the sound — live radio, TV, a nearby speaker, or music already on your device." },
  { n: "02", icon: Radio,      tone: "am-tile-peach",    title: "Tap LiveLink",      copy: "Within seconds AudioMine identifies the playing track and verifies whether it contains Reward-Performance Markers (RPM)." },
  { n: "03", icon: Sparkles,   tone: "am-tile-lemon",    title: "See the mining stats", copy: "If the media is RPM-enabled you get a full song summary and worldwide audio-mining stats before you tap Play." },
  { n: "04", icon: Play,       tone: "am-tile-mint",     title: "Play, watch, interact", copy: "The magic happens. Live lyrics, videos, photo slideshows and games — all augmented over drastically improved D1 Audio fidelity." },
  { n: "05", icon: Trophy,     tone: "am-tile-lavender", title: "Earn RPM",          copy: "Listen deeper. Play the lyric-bubble game. Watch videos. The more you interact, the more RPM tokens you mine." },
  { n: "06", icon: ImageIcon,  tone: "am-tile-sky",      title: "Bring your action", copy: "Photo mode syncs your phone gallery into a live slideshow that moves with the music's dynamics." },
];

export default function HowItWorks() {
  return (
    <main data-testid="page-how-it-works" className="pt-24 md:pt-32">
      {/* HERO */}
      <section className="relative">
        <div className="container relative">
          <div className="am-blob am-blob-orange" style={{ right: "10%", top: "-40px" }} />
          <div className="max-w-4xl">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-4 py-1.5 text-[11px] uppercase tracking-[0.24em] text-primary font-semibold border border-black/5">
                <Sparkles className="h-3.5 w-3.5" /> How it works
              </span>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="mt-6 font-display font-bold tracking-tight text-[clamp(2.6rem,6.5vw,5.75rem)] leading-[0.98]">
                Six steps from <span className="am-sunset-text">tap</span>
                <br /><span className="font-serif-editorial italic font-normal">to payout.</span>
              </h1>
            </Reveal>
            <Reveal delay={160}>
              <p className="mt-6 text-lg md:text-xl text-foreground/65 max-w-2xl leading-relaxed">
                AudioMine is designed to feel like magic — but under the hood it's a music identification, gamification, and reward engine you can run from your pocket.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STEP CARDS GRID */}
      <section className="py-24 md:py-32">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STEPS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.n} delay={i * 80}>
                  <div className={`relative overflow-hidden rounded-[32px] ${s.tone} p-8 md:p-10 aspect-[4/5] flex flex-col justify-between`}>
                    <div className="flex items-start justify-between">
                      <span className="font-display font-bold text-6xl text-foreground/80">{s.n}</span>
                      <div className="h-12 w-12 rounded-2xl bg-white/70 backdrop-blur flex items-center justify-center text-foreground">
                        <Icon className="h-5 w-5" />
                      </div>
                    </div>
                    <div>
                      <h3 className="font-display font-bold text-2xl md:text-3xl leading-tight">{s.title}</h3>
                      <p className="mt-3 text-foreground/70 text-[15px] leading-relaxed">{s.copy}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24 md:pb-32">
        <div className="container">
          <Reveal>
            <div className="am-soft rounded-[36px] p-10 md:p-16 text-center flex flex-col items-center gap-6">
              <h2 className="font-display font-bold text-3xl md:text-5xl max-w-2xl">Now do it with <span className="am-sunset-text">real music.</span></h2>
              <p className="text-foreground/60 max-w-xl">Tell us where you fit — artist, fan, or partner — and we'll get you moving.</p>
              <Link to="/contact">
                <Button data-testid="how-cta" size="lg" className="rounded-full h-14 px-10 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-[0_10px_30px_-8px_rgba(250,37,104,0.55)]">
                  Get started <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
